/* eslint-disable @typescript-eslint/no-explicit-any */
import { TranslateService } from '@ngx-translate/core';

// ****************************************************************
// i18n Translation for Facades
// ****************************************************************

export type KeyInfo = { key: string; en: string };
export type TranslateFn = (
  defaultValue: string,
  key: string | KeyInfo,
  params?: Record<string, unknown>
) => string;
export type ShowLog = (passThru?: any) => any;

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function isKeyInfo(node: any): node is KeyInfo {
  return typeof node === 'object' && 'en' in node;
}

/**
 * String interpolation
 * Replace {{key}} with params[key] in target string
 */
const interpolateString = (
  target: string,
  params?: { [key: string]: any }
): string =>
  params
    ? target.replace(/{{\s*(\w+)\s*}}/g, (_, key) => params[key] || '')
    : target;

/**
 * Accessor to ngx-translate service lookups
 * Extra features:
 *  - comma-separated keys for progressive lookups
 *  - default value if no translation is found
 *  - logging of missing translations
 */
export const translateWithDefaults = (
  translate: TranslateService, // ngx-translate service
  tracker?: boolean | LogTracker
): TranslateFn => {
  const t: TranslateFn = (
    defaultValue: string,
    source: string | KeyInfo,
    params?: Record<string, unknown>
  ): string => {
    defaultValue = interpolateString(defaultValue, params) || '';

    const key: string = isKeyInfo(source) ? source.key : source;
    const allKeys = key.replace(/\s+/g, '').split(','); // This will remove all whitespace

    // Find "most recent" translation
    let value = '';
    let selectedKey = '';

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < allKeys.length; i++) {
      if (!value && allKeys[i]) {
        selectedKey = allKeys[i];
        value = translate.instant(selectedKey, params) || '';

        // If ngx/translate returns the key, it means the translation is missing
        if (value === selectedKey) value = '';
      }

      if (!value) {
        if (tracker) {
          if (tracker === true) {
            const target = { key, defaultValue };
            console.error(`i18n: Missing translation for`, target);
          } else {
            tracker.track(selectedKey, defaultValue);
          }
        }
      }
    }

    return value || defaultValue;
  };

  return t;
};

export type i18nKeyRegistry = Record<
  string,
  string | Record<string, string | KeyInfo>
>;
export type i18nKeyValues = Record<string, string>;
export type ScanOptions = {
  translate: TranslateService; // ngx-translate service
  context?: string;
};

/**
 * Use ngx/translate to check the localization bundles
 * for the specified keys.
 *
 * - Group missing keys by context.
 * - Auto-report to console.
 */
export const scanBundlesFor = (
  keyTree: Record<string, any>,
  { context, translate }: ScanOptions
) => {
  const registry = keyTree as i18nKeyRegistry;
  const [t, report] = i18nFor(`i18n (${context || 'global'})`, translate);
  const visitor: Visitor = (node, key) => {
    if (isKeyInfo(node[key])) {
      t(node[key].en, node[key].key);
    }
  };

  report(traverse(registry, visitor));
};

/**
 * Accessor to ngx-translate service lookups
 * Logs missing translations [for `context`] to cache
 *
 * NOTE: Requires explicit `report()` to show logs
 */
export const i18nFor = (
  context: string,
  translate: TranslateService // ngx-translate service
): [TranslateFn, ShowLog] => {
  const tracker = new LogTracker(context);
  const t: TranslateFn = translateWithDefaults(translate, tracker);

  return [t, tracker.report.bind(tracker)];
};

export const buildTranslateFn = (
  translate: TranslateService,
  enableLog = false
): TranslateFn => translateWithDefaults(translate, enableLog);

// *************************************************
// Internal Utils
//
// NOTE:
//
// These are the same classes defined in `tracker.utils.ts` + `layout-configuration.utils.ts`;
// duplicated here until Nx migration and lib refactors
// *************************************************

export type MissingKeys = Record<string, string>;
export type TrackerOptions = { message?: string; key: string; value: string };

export class LogTracker {
  public message = 'keys missing';

  protected registry: Readonly<MissingKeys>[] = [];
  protected key = 'i18n';
  protected value = 'en';

  constructor(
    protected title: string,
    options?: TrackerOptions
  ) {
    if (options) {
      this.key = options.key;
      this.value = options.value;
      this.message = options.message || '';
    }
  }

  track(key: string, value: string) {
    if (!this.findEntryBy(key)) {
      this.registry.push({
        [this.key]: key,
        [this.value]: value,
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  report(passThru?: any): any {
    if (this.registry.length) {
      const msg = `${this.title} ${this.message}:`;
      console.warn(msg, this.registry);
    }
    return passThru;
  }

  protected findEntryBy(key: string): MissingKeys | null {
    return this.registry.reduce(
      (result: MissingKeys | null, it: MissingKeys) =>
        result || it[this.key] === key ? it : null,
      null
    );
  }
}

// *************************************************
// Tree Utils
// *************************************************

type LayoutNode = Record<string, any>;
type Visitor = (node: any, key: string, parent?: any) => void;

// Depth-first node tree scanning
const traverse = (node: any, visit: Visitor, parent?: LayoutNode) => {
  for (const key in node) {
    if (typeof node[key] === 'object') {
      visit(node, key, parent);
      traverse(node[key], visit, node);
    } else {
      visit(node, key, parent);
    }
  }
  return node;
};
